import { IconButton, Modal, Tooltip, Typography, Box } from "@mui/material";
import Track from "./Track";
import { CardModalProps } from "./types";
import { NavLink } from "react-router-dom";
import { DescriptionContainer, FlexContainer } from "./styles";
import { ContributorsContainer, ModalBox } from "./styles";
import { useAuth0 } from "@auth0/auth0-react";
import { ContainedButton } from "../UserProfile/styles";
import { useCallback, useEffect, useState } from "react";
import { NftTrack } from "./types";
import Mixpanel from "../../utils/mixpanel";
import FacebookIcon from "@mui/icons-material/Facebook";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import TwitterIcon from "@mui/icons-material/Twitter";
import { FacebookShareButton } from "react-share";
import { TwitterShareButton, EmailShareButton } from "react-share";
import { modalStyles as styles } from "./styles";
import { shortenLink } from "../../services/utilService";
import MessageIcon from "@mui/icons-material/Message";
import { toast } from "react-toastify";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { APP_BACKGROUND_COLOR, FONT_FAMILY } from "../../theme";

const openseaURL = process.env.REACT_APP_OPENSEA_URL ?? "";
const erc721ContractAddress = process.env.REACT_APP_ECR721 ?? "";

export const CardModal: React.FC<CardModalProps> = ({
  open,
  handleClose,
  nft,
  handleCreateGroupChat,
  isTrack,
}) => {
  const [bitlyUrl, setBitlyUrl] = useState<string>("");
  const { description, contributors } = nft;
  const { loginWithRedirect, isAuthenticated, getIdTokenClaims } = useAuth0();
  const [currentUserId, setCurrentUserId] = useState("");
  const [track, setTrack] = useState<NftTrack>({
    id: nft.id ?? 1,
    audio: nft.song,
    waveSurfer: undefined,
    isPlaying: false,
    image: nft.image,
    title: nft.title,
    subtitle:
      nft.artist === ""
        ? contributors.map((c) => c.getUsername()).join(", ")
        : nft.artist,
    genre: nft.genres ? (nft.genres.length > 0 ? nft.genres[0] : "") : "",
  });

  // shortening the URLs using bitly api
  useEffect(() => {
    const urlToBitly = async () => {
      try {
        if (window.location.hostname === "localhost") {
          const response = await shortenLink(
            `https://app-dev.ommuse.com/nft/${nft.txnId}`
          );
          setBitlyUrl(response.data.link);
        } else {
          const response = await shortenLink(
            `${window.location.href.replace("explore", "")}nft/${nft.txnId}`
          );
          setBitlyUrl(response.data.link);
        }
      } catch (error) {
        console.log("Error for bitly URL in CardModal" + error);
      }
    };
    urlToBitly();
  }, [nft.txnId]);

  useEffect(() => {
    (async () => {
      if (isAuthenticated) {
        const idToken = await getIdTokenClaims();
        if (idToken) {
          setCurrentUserId(idToken.sub);
        }
      }
    })();
  }, [getIdTokenClaims, isAuthenticated, track.id]);

  const otherContributors = contributors.filter(
    (c) => c.getUserId() !== currentUserId
  );

  const setAudioTrack = useCallback(
    (_id: number, audioTrack: WaveSurfer) => {
      if (!track.waveSurfer) {
        setTrack({ ...track, waveSurfer: audioTrack });
      }
    },
    [track]
  );

  const handlePlay = (_id: number) => {
    track.waveSurfer?.playPause();
    Mixpanel.track("play_track_card", { id: track.id });
    setTrack({
      ...track,
      isPlaying: track.waveSurfer?.isPlaying() ?? false,
    });
  };

  const handleStop = (_id: number) => {
    track.waveSurfer?.stop();
    Mixpanel.track("stop_track_card", { id: track.id });
    setTrack({
      ...track,
      isPlaying: track.waveSurfer?.isPlaying() ?? false,
    });
  };

  const handleCopy = async () => {
    navigator.clipboard.writeText(bitlyUrl);
    toast.success("Link copied!");
  };

  const onClose = () => {
    Mixpanel.track("close_track_card", { id: track.id });
    setTrack({ ...track, isPlaying: false, waveSurfer: undefined });
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <ModalBox sx={{ backgroundColor: APP_BACKGROUND_COLOR }}>
        <Box sx={{ backgroundColor: APP_BACKGROUND_COLOR }}>
          <Track
            track={track}
            setAudioTrack={setAudioTrack}
            handlePlay={handlePlay}
            handleStop={handleStop}
          />
          <FlexContainer>
            <DescriptionContainer>
              <Typography sx={styles.description}>Description</Typography>
              <Typography style={{ fontFamily: FONT_FAMILY }}>
                {description}
              </Typography>
              <div style={{ margin: "10px 0px" }}>
                {isAuthenticated ? (
                  otherContributors.length >= 1 && <></>
                ) : (
                  <ContainedButton
                    size="small"
                    variant="contained"
                    onClick={() =>
                      loginWithRedirect({
                        appState: { targetUrl: "/" },
                      })
                    }
                    style={{ fontFamily: FONT_FAMILY }}
                  >
                    Log in | Sign up
                  </ContainedButton>
                )}
              </div>
            </DescriptionContainer>
            <ContributorsContainer>
              <Typography sx={styles.contributorsHeading}>
                Contributors
              </Typography>
              <div style={styles.contributorsPhotoContainer}>
                {contributors
                  .filter((c) => c.getUsername())
                  .map((contributor) => {
                    return (
                      <NavLink
                        key={contributor.getUserId()}
                        to={`/user-profile/${contributor.getUsername()}`}
                        style={styles.contributorNavLink}
                      >
                        <Tooltip
                          title={contributor.getUsername()}
                          placement="top"
                        >
                          <img
                            style={styles.contributorsPhotos}
                            src={
                              contributor.getProfilePicture()?.includes("http")
                                ? contributor.getProfilePicture()
                                : `/assets/images/logo512.png`
                            }
                            alt=""
                          />
                        </Tooltip>
                      </NavLink>
                    );
                  })}
                <Tooltip title="Send message">
                  <IconButton
                    sx={{ color: "white", zIndex: 1000, marginRight: -1 }}
                    aria-label="master"
                    onClickCapture={handleCreateGroupChat}
                  >
                    <MessageIcon />
                  </IconButton>
                </Tooltip>
              </div>
              {!isTrack && (
                <div
                  style={{
                    display: "flex",
                    backgroundColor: APP_BACKGROUND_COLOR,
                  }}
                >
                  {window.location.href.includes("user-profile") ? (
                    <></>
                  ) : (
                    <>
                      <div style={styles.openSeaLinkContainer}>
                        <span>
                          <a
                            href={`${openseaURL}/${erc721ContractAddress}/${nft.id}`}
                            target="_blank"
                            rel="noreferrer"
                            style={styles.openSeaUrl}
                          >
                            <img
                              src="/assets/images/opensea.svg"
                              alt="opensea"
                              style={styles.openSeaImage}
                            />
                          </a>
                        </span>
                      </div>
                      <Tooltip title="Copy" placement="top">
                        <IconButton
                          disableFocusRipple
                          style={{ marginRight: 5 }}
                          onClick={handleCopy}
                          size="small"
                        >
                          <ContentCopyIcon color="primary" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Facebook" placement="top">
                    <FacebookShareButton url={bitlyUrl} hashtag={"#ommuse"}>
                      <FacebookIcon
                        color="primary"
                        style={{ marginRight: 10 }}
                      />
                    </FacebookShareButton>
                  </Tooltip>
                  <Tooltip title="Twitter" placement="top">
                    <TwitterShareButton
                      url={bitlyUrl}
                      hashtags={["OmMuse"]}
                      via="OmMuseMusic"
                    >
                      <TwitterIcon
                        color="primary"
                        style={{ marginRight: 10 }}
                      />
                    </TwitterShareButton>
                  </Tooltip>
                  <Tooltip title="Email" placement="top">
                    <EmailShareButton
                      url={bitlyUrl}
                      subject="Music NFT on OmMuse"
                      body={`Hello, check out this music NFT:`}
                      openShareDialogOnClick={true}
                      onClick={() => {}}
                    >
                      <MailOutlineIcon
                        color="primary"
                        style={{ marginRight: 10 }}
                      />
                    </EmailShareButton>
                  </Tooltip>
                </div>
              )}
            </ContributorsContainer>
          </FlexContainer>
        </Box>
      </ModalBox>
    </Modal>
  );
};
